// src/pages/FashnAssetsPage.jsx
import React, { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector }    from 'react-redux'
import { fetchAssets, removeAsset }    from '../store/reducers/virtualSwapReducer'
import { trackVirtualAction }          from '../store/reducers/customerVirtualActivitySlice'
import {
  recordProductView,
  recordProductDownload,
  recordProductShare
} from '../store/reducers/productVirtualActivitySlice'
import { useNavigate }                 from 'react-router-dom'
import {
  AiOutlineEye,
  AiOutlineDownload,
  AiOutlineShareAlt,
  AiOutlineClose,
  AiOutlineLeft,
  AiOutlineRight
} from 'react-icons/ai'
import { motion, AnimatePresence }     from 'framer-motion'
import toast                           from 'react-hot-toast'
import Heders                          from '../components/Headers'
import { jsPDF }                       from 'jspdf'
import { base_url }                    from '../utils/config'

const MAX_ASSETS = 30

export default function FashnAssetsPage() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const userId   = useSelector(s => s.auth.userInfo.id)
  const { assets, loading, error } = useSelector(s => s.virtualSwap)

  const [confirmDeleteId, setConfirmDeleteId] = useState(null)
  const [lightboxIndex, setLightboxIndex]     = useState(null)
  const touchStartX = useRef(0)

  // 1️⃣ track page visit & load assets
  useEffect(() => {
    dispatch(trackVirtualAction({
      userId,
      actionType: 'visit_assets_page'
    }))
    dispatch(fetchAssets())
  }, [dispatch, userId])

  // 2️⃣ confirm & delete asset, track success/failure
  const reallyDelete = async () => {
    try {
      await dispatch(removeAsset(confirmDeleteId)).unwrap()
      toast.success('Asset removed')
      dispatch(trackVirtualAction({
        userId,
        actionType: 'delete_asset',
        metadata: { assetId: confirmDeleteId }
      }))
    } catch {
      toast.error('Failed to delete asset')
      dispatch(trackVirtualAction({
        userId,
        actionType: 'delete_asset_failed',
        metadata: { assetId: confirmDeleteId }
      }))
    } finally {
      setConfirmDeleteId(null)
    }
  }

  // 3️⃣ download an asset, track download
  const handleDownload = async (asset) => {
    try {
      const res  = await fetch(asset.imageUrl)
      const blob = await res.blob()
      const blobUrl = URL.createObjectURL(blob)
      const a    = document.createElement('a')
      a.href     = blobUrl
      a.download = 'fashn-tryon.png'
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(blobUrl)
      toast.success('Downloaded')

      dispatch(trackVirtualAction({
        userId,
        actionType: 'download_asset'
      }))
      // product-level download
      dispatch(recordProductDownload(asset.productId))
    } catch {
      toast.error('Download failed')
      dispatch(trackVirtualAction({
        userId,
        actionType: 'download_asset_failed'
      }))
    }
  }

  // 4️⃣ share an asset, track share
  const handleShare = async (asset) => {
    try {
      const res  = await fetch(asset.imageUrl)
      const blob = await res.blob()
      const file = new File([blob], 'fashn-tryon.png', { type: blob.type })

      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file] })
      } else if (navigator.share) {
        const shareUrl = `${window.location.origin}/product/details/${asset.productId}`
        await navigator.share({
          title: asset.name,
          text: 'Check out this try‑on image!',
          url: shareUrl
        })
      } else {
        const shareUrl = `${window.location.origin}/product/details/${asset.productId}`
        await navigator.clipboard.writeText(shareUrl)
        toast.success('Link copied')
      }

      dispatch(trackVirtualAction({
        userId,
        actionType: 'share_asset',
        metadata: { assetId: asset._id }
      }))
      // product-level share
      dispatch(recordProductShare(asset.productId))
    } catch {
      toast.error('Share failed')
      dispatch(trackVirtualAction({
        userId,
        actionType: 'share_asset_failed',
        metadata: { assetId: asset._id }
      }))
    }
  }

  // 5️⃣ download all assets PDF, track bulk download
  const downloadAll = async () => {
    if (!assets.length) return
    dispatch(trackVirtualAction({
      userId,
      actionType: 'download_all_assets',
      metadata: { count: assets.length }
    }))

    const doc = new jsPDF()
    let y = 10

    for (let i = 0; i < assets.length; i++) {
      const a = assets[i]
      const res = await fetch(a.imageUrl)
      const blob = await res.blob()
      const dataUrl = await new Promise(resolve => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.readAsDataURL(blob)
      })

      doc.addImage(dataUrl, 'PNG', 10, y, 40, 40)
      doc.text(`Name: ${a.name}`, 55, y + 10)
      doc.text(`Category: ${a.category}`, 55, y + 20)
      doc.text(`Shop: ${a.shopName}`, 55, y + 30)

      const linkY = y + 55
      const productUrl = `${window.location.origin}/product/details/${a.productId}`
      doc.textWithLink('View Product', 10, linkY, { url: productUrl })

      y += 70
      if (y > 240 && i < assets.length - 1) {
        doc.addPage()
        y = 10
      }
    }

    doc.save('fashn-assets.pdf')
  }

  if (loading) return <p className="p-8 text-center">Loading…</p>
  if (error)   return <p className="p-8 text-center text-red-600">{error}</p>

  if (!assets.length) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-600">No saved assets yet.</p>
        <button
           onClick={() => navigate(-1)}
          className="mt-4 text-blue-600 hover:underline"
        >
          Go Back to Try‑On
        </button>
      </div>
    )
  }

  const remaining = MAX_ASSETS - assets.length
  const openLightbox  = idx => {
    setLightboxIndex(idx)
    // product-level view for that image’s product
    dispatch(recordProductView(assets[idx].productId))
  }
  const closeLightbox = ()  => setLightboxIndex(null)
  const showPrev      = ()  => setLightboxIndex(i => (i - 1 + assets.length) % assets.length)
  const showNext      = ()  => setLightboxIndex(i => (i + 1) % assets.length)

  return (
    <div className="min-h-screen bg-gray-50 pb-32 relative overflow-hidden">
      <Heders />

      <main className="max-w-5xl mx-auto px-4 pt-24 pb-12 space-y-4">
        <h1 className="text-2xl font-bold">Your Fashn Assets</h1>
        <p className="text-gray-600">
          You have {assets.length} saved {assets.length === 1 ? 'image' : 'images'}.
        </p>
        <p className="text-sm text-gray-500">
          You can save {remaining} more {remaining === 1 ? 'image' : 'images'}.
        </p>

        <button
          onClick={downloadAll}
          className="block mx-auto text-blue-600 hover:underline"
        >
          Download All Assets
        </button>

        <section className="grid gap-6 grid-cols-4 md:grid-cols-2 lg:grid-cols-4">
          {assets.map((asset, idx) => (
            <motion.div
              key={asset._id}
              whileHover={{ scale: 1.02 }}
              className="relative bg-white rounded-lg shadow overflow-hidden"
            >
              <img
                src={asset.imageUrl}
                alt={asset.name}
                className="w-full h-56 object-cover cursor-pointer"
                onClick={() => openLightbox(idx)}
              />

              <button
                onClick={e => { e.stopPropagation(); setConfirmDeleteId(asset._id) }}
                className="absolute top-2 left-2 text-red-500 bg-white bg-opacity-80 p-1 rounded-full z-10"
              >
                <AiOutlineClose size={18} />
              </button>

              <div className="absolute top-2 right-2 flex space-x-2 z-10">
                <AiOutlineEye
                  size={20}
                  className="text-white bg-black bg-opacity-50 p-1 rounded-full cursor-pointer"
                  onClick={e => { e.stopPropagation(); openLightbox(idx) }}
                />
                <AiOutlineDownload
                  size={20}
                  className="text-white bg-black bg-opacity-50 p-1 rounded-full cursor-pointer"
                  onClick={e => { e.stopPropagation(); handleDownload(asset) }}
                />
                <AiOutlineShareAlt
                  size={20}
                  className="text-white bg-black bg-opacity-50 p-1 rounded-full cursor-pointer"
                  onClick={e => { e.stopPropagation(); handleShare(asset) }}
                />
              </div>

              <div className="p-4">
                <h2 className="text-lg font-semibold truncate">{asset.name}</h2>
                <p className="text-sm text-gray-500 truncate">
                  {asset.category} · {asset.shopName}
                </p>
                <div className="mt-2 flex items-baseline">
                  {asset.discount > 0 && (
                    <span className="line-through text-gray-400 mr-2">
                      ₹{asset.price}
                    </span>
                  )}
                  <span className="text-xl font-bold text-green-600">
                    ₹{Math.floor(asset.price * (1 - (asset.discount||0)/100))}
                  </span>
                </div>
                <button
                  onClick={() => navigate(`/product/details/${asset.productId}`)}
                  className="mt-3 w-full text-sm bg-blue-600 text-white py-2 rounded hover:bg-blue-700 transition"
                >
                  Check Product Details
                </button>
              </div>
            </motion.div>
          ))}
        </section>

        <button
          onClick={() => navigate('/virtual-swap')}
          className="block mx-auto text-blue-600 hover:underline"
        >
          Generate More Try‑Ons
        </button>
      </main>

      {/* DELETE CONFIRM */}
      <AnimatePresence>
        {confirmDeleteId && (() => {
          const a = assets.find(x => x._id === confirmDeleteId)
          return (
            <motion.div
              key="confirm"
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            >
              <motion.div
                className="bg-white rounded-lg p-6 max-w-sm w-full"
                initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              >
                <h2 className="text-lg font-semibold mb-4">Remove This Asset?</h2>
                <div className="flex items-center mb-4">
                  <img src={a.imageUrl} className="w-16 h-16 object-cover rounded mr-4" />
                  <div>
                    <p className="font-medium">{a.name}</p>
                    <p className="text-sm text-gray-500">{a.category}</p>
                  </div>
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => setConfirmDeleteId(null)}
                    className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={reallyDelete}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Yes, remove
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )
        })()}
      </AnimatePresence>

      {/* LIGHTBOX */}
      <AnimatePresence>
        {lightboxIndex !== null && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={closeLightbox}
            onTouchStart={e => { touchStartX.current = e.touches[0].clientX }}
            onTouchEnd={e => {
              const dx = e.changedTouches[0].clientX - touchStartX.current
              if (dx > 50) showPrev()
              else if (dx < -50) showNext()
            }}
          >
            <motion.div
              className="relative w-11/12 sm:w-3/4 md:w-2/3 lg:w-1/2 max-w-2xl max-h-[90vh] bg-white rounded-lg overflow-hidden"
              initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              onClick={e => e.stopPropagation()}
            >
              <button
                className="absolute top-2 right-2 text-white bg-black bg-opacity-50 p-1 rounded-full z-20"
                onClick={closeLightbox}
              >
                <AiOutlineClose size={20} />
              </button>
              <AiOutlineLeft
                size={30}
                className="absolute top-1/2 left-2 -translate-y-1/2 text-white bg-black bg-opacity-50 p-1 rounded-full cursor-pointer z-20"
                onClick={e => { e.stopPropagation(); showPrev() }}
              />
              <AiOutlineRight
                size={30}
                className="absolute top-1/2 right-2 -translate-y-1/2 text-white bg-black bg-opacity-50 p-1 rounded-full cursor-pointer z-20"
                onClick={e => { e.stopPropagation(); showNext() }}
              />
              <img
                src={assets[lightboxIndex].imageUrl}
                alt={assets[lightboxIndex].name}
                className="w-full max-h-[85vh] object-contain bg-gray-100"
              />
              <div className="flex justify-around p-4 bg-gray-50">
                <button
                  onClick={() => handleDownload(assets[lightboxIndex])}
                  className="flex items-center space-x-1 text-green-600"
                >
                  <AiOutlineDownload size={20} /><span>Download</span>
                </button>
                <button
                  onClick={() => handleShare(assets[lightboxIndex])}
                  className="flex items-center space-x-1 text-purple-600"
                >
                  <AiOutlineShareAlt size={20} /><span>Share</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
